import { useState, useEffect, useCallback } from 'react'
import { useAuthStore } from '@/store/useAuthStore'
import {
  subscribeToBidHistory,
  anonymizeBidHistory,
  placeBid as placeBidApi,
  setAutoBid as setAutoBidApi,
  getMinimumNextBid,
  getMinimumBidIncrement,
} from '@/services/api/bids'
import type { Bid, AnonymizedBid } from '@/types/bid'
import type { Listing } from '@/types/listing'

interface UseAuctionOptions {
  listing: Listing | null
}

export function useAuction({ listing }: UseAuctionOptions) {
  const { user } = useAuthStore()
  const [bids, setBids] = useState<Bid[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isPlacingBid, setIsPlacingBid] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const listingId = listing?.id

  // Subscribe to bid history
  useEffect(() => {
    if (!listingId) {
      setBids([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    const unsubscribe = subscribeToBidHistory(listingId, (data) => {
      setBids(data)
      setIsLoading(false)
    })

    return () => unsubscribe()
  }, [listingId])

  // Anonymized bid history for display
  const anonymizedBids: AnonymizedBid[] = anonymizeBidHistory(bids, user?.uid)

  // Current auction state
  const currentBid = (listing as any)?.currentBid || listing?.price || 0
  const minimumBid = getMinimumNextBid(currentBid)
  const bidIncrement = getMinimumBidIncrement(currentBid)
  const bidCount = (listing as any)?.bidCount || 0
  const highBidderId = (listing as any)?.highBidderId
  const isHighBidder = user?.uid === highBidderId
  const reservePrice = (listing as any)?.reservePrice
  const reserveMet = (listing as any)?.reserveMet || (reservePrice ? currentBid >= reservePrice : true)
  const hasAutoBid = !!(listing as any)?.maxBid && (listing as any)?.maxBidderId === user?.uid
  const userMaxBid = hasAutoBid ? (listing as any)?.maxBid : null

  // Check if auction has ended
  const expiresAt = listing?.expiresAt
  const auctionEnded = (listing as any)?.auctionEnded ||
    (expiresAt && expiresAt.toMillis() < Date.now())

  // Place a bid
  const placeBid = useCallback(
    async (amount: number) => {
      if (!listingId || !user) {
        setError('Please sign in to place a bid')
        return { success: false, message: 'Please sign in to place a bid' }
      }

      setIsPlacingBid(true)
      setError(null)

      try {
        const result = await placeBidApi(
          listingId,
          user.uid,
          user.displayName || 'Anonymous',
          amount
        )

        if (!result.success) {
          setError(result.message)
        }

        return result
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to place bid'
        setError(message)
        return { success: false, message }
      } finally {
        setIsPlacingBid(false)
      }
    },
    [listingId, user]
  )

  // Set auto-bid
  const setAutoBid = useCallback(
    async (maxAmount: number) => {
      if (!listingId || !user) {
        setError('Please sign in to set auto-bid')
        return { success: false, message: 'Please sign in to set auto-bid' }
      }

      setIsPlacingBid(true)
      setError(null)

      try {
        const result = await setAutoBidApi(
          listingId,
          user.uid,
          user.displayName || 'Anonymous',
          maxAmount
        )

        if (!result.success) {
          setError(result.message)
        }

        return result
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to set auto-bid'
        setError(message)
        return { success: false, message }
      } finally {
        setIsPlacingBid(false)
      }
    },
    [listingId, user]
  )

  return {
    // Bid history
    bids: anonymizedBids,
    isLoading,

    // Auction state
    currentBid,
    minimumBid,
    bidIncrement,
    bidCount,
    isHighBidder,
    reservePrice,
    reserveMet,
    auctionEnded,
    hasAutoBid,
    userMaxBid,

    // Actions
    placeBid,
    setAutoBid,
    isPlacingBid,
    error,
  }
}
